"use client"
import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { Calendar2Icon, NewText, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import { RentProjectJoinBlock } from "utils/projects"

type Props = {
  locale: string
  data: RentProjectJoinBlock
  bmbyMeetingId?: string
}
const ContainerForBG = styled.div`
  --benefits-black: #011727;
  position: relative;
  display: flex;
  z-index: 1;
  box-shadow: 0 0 12.9px 2px rgba(154, 153, 153, 0.3);
  background-color: #060720;
  &::before {
    content: "";
    display: flex;
    background-image: url("/images/rent4-meeting-bg.png");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    aspect-ratio: 10 / 9;
    width: 100%;
    max-width: 400px;
    height: 100%;

    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    &::after {
      content: "";
      display: flex;
      background-image: url("/images/rent4-meeting-bg.png");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      aspect-ratio: 10 / 9;
      width: 100%;
      max-width: 400px;
      height: 100%;

      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
    }
  }
`
const StyledCMSchedule = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-block: 60px;
  padding-inline: 20px;
  width: 100%;
  color: white;
  position: relative;
  z-index: 2;
`
const StyledP = styled(NewText)`
  letter-spacing: -0.36px;
  margin-bottom: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    letter-spacing: -0.4px;
    margin-bottom: 16px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
  margin-top: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 28px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
  }
`

const AWrapper = styled.a`
  order: -1;
  ${(props) => props.theme.breakpoints.up("md")} {
    order: unset;
    pointer-events: none;
  }
`
const ActionButtonBlue = styled(ActionButton)`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);
  gap: 4px;
  min-width: 327px;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 300px;
    width: unset;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const ActionButtonWhite = styled(ActionButton)`
  --theme-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
  background-color: white;
  min-width: 327px;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 300px;
    width: unset;
  }
  &:hover {
    background-color: var(--theme-color);
    color: white;
  }
`
const ContactMeetingSchedule = ({ locale, data, bmbyMeetingId }: Props) => {
  const { ref, inView } = useInView(useInViewConfig)
  const title = data?.title
  const subTitle = data?.subTitle
  const buttonBlue = data?.buttonBlue?.[0]
  const buttonWhite = data?.buttonWhite
  const shouldRender = title || subTitle || buttonBlue?.name

  const handleScheduleBmbyMeeting = () => {
    const lang = locale === "en" ? "en" : "he"
    const url = `https://bmbyscheduler.azurewebsites.net/?meeting=${bmbyMeetingId}&lang=${lang}`
    const popup = window.open(url, "bmbyScheduler", "width=940,height=500")
    if (!popup) return // in case popup was blocked

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer) // ✅ cleanup
        // success logic here
      }
    }, 500)

    // ✅ extra safety: cleanup if user leaves page
    window.addEventListener("beforeunload", () => clearInterval(timer))
  }

  if (!shouldRender) return null

  return (
    <TransitionUp $inView={inView} ref={ref} $delay={transitionDelayTime}>
      <ContainerForBG>
        <StyledCMSchedule>
          {title && (
            <NewText
              component="h3"
              desktopFontSize={60}
              desktopFontWeight={400}
              desktopLineHeight={"120%"}
              mobileLineHeight={"120%"}
              mobileFontSize={32}
              mobileFontWeight={500}
              color="inherit"
              sx={{
                margin: 0,
                marginBottom: "8px",
              }}
            >
              {title}
            </NewText>
          )}
          {subTitle && (
            <StyledP
              component="p"
              desktopFontSize={24}
              desktopFontWeight={300}
              desktopLineHeight={"150%"}
              mobileLineHeight={"150%"}
              mobileFontSize={18}
              color="inherit"
              sx={{
                letterSpacing: {
                  xs: "-0.36px",
                  md: "-0.48px",
                },
              }}
            >
              {subTitle}
            </StyledP>
          )}
          <ActionButtons dir={locale}>
            {buttonWhite && bmbyMeetingId && (
              <ActionButtonBlue
                variant="outlined"
                color="inherit"
                type="button"
                onClick={handleScheduleBmbyMeeting}
              >
                <Calendar2Icon />
                {buttonWhite}
              </ActionButtonBlue>
            )}
            {buttonBlue?.url && buttonBlue?.name && (
              <AWrapper href={buttonBlue.url}>
                <ActionButtonWhite variant="contained" color="primary">
                  {buttonBlue.name}
                </ActionButtonWhite>
              </AWrapper>
            )}
          </ActionButtons>
        </StyledCMSchedule>
      </ContainerForBG>
    </TransitionUp>
  )
}
export default ContactMeetingSchedule
